import { formatDistanceToNowStrict } from "date-fns";
import { chatEntryRoutingHintMap, chatEntryTitleMap } from "@/config/chat.config";
import type {
  GrowthPattern,
  GrowthPatternAccent,
  LanguageShift,
  NarrativeEntry,
  SessionMirrorPrompt,
} from "@/config/insightsSample";
import { chatHistoryService } from "@/services/chatHistoryService";
import {
  answeredInsightIds,
  listMirrorInsightResponses,
} from "@/services/sessionMirrorInsightService";
import type { ChatHistoryRecord } from "@/types/history";
import type { UserSession } from "@/types/session";
import type { ChatEntry } from "@/types/chat";
import { plainUserText } from "@/lib/plainUserText";

export type DerivedInsightsDashboard = {
  mirrorQueue: SessionMirrorPrompt[];
  patterns: GrowthPattern[];
  languageShifts: LanguageShift[];
  narrativeEntries: NarrativeEntry[];
  sessionsWithMessages: number;
  milestonesCount: number;
};

const PILLAR_MODES: Record<string, ChatEntry[]> = {
  "self-regulation": ["reclaim_my_balance", "reset_my_nervous_system"],
  "relational-curiosity": ["connect_with_students"],
  "charged-navigation": ["strengthen_my_voice", "navigate_parent_relationships", "lead_with_clarity", "leading_your_staff"],
  "agency-authorship": ["expand_my_impact", "get_coaching", "ai_mentor"],
};

const PILLAR_META: Record<
  string,
  { categoryLabel: string; accent: GrowthPatternAccent; defaultTitle: string; defaultFooter: string }
> = {
  "self-regulation": {
    categoryLabel: "Self-regulation",
    accent: "emerald",
    defaultTitle: "Finding steadiness in the hard moments",
    defaultFooter: "Sessions in balance and reset spaces feed this pattern.",
  },
  "relational-curiosity": {
    categoryLabel: "Relational curiosity",
    accent: "teal",
    defaultTitle: "Staying curious about the people in front of you",
    defaultFooter: "Student conversations show up here as you use them.",
  },
  "charged-navigation": {
    categoryLabel: "Charged navigation",
    accent: "gold",
    defaultTitle: "Moving through high-stakes conversations",
    defaultFooter: "Parent and colleague chats shape this arc.",
  },
  "agency-authorship": {
    categoryLabel: "Agency + authorship",
    accent: "violet",
    defaultTitle: "Acting from intention, not just reaction",
    defaultFooter: "Coaching and impact sessions grow this thread.",
  },
};

export { plainUserText } from "@/lib/plainUserText";

export function extractUserMessages(records: ChatHistoryRecord[]): string[] {
  return records
    .filter((r) => r.message.role === "user")
    .map((r) => plainUserText(r.message.content))
    .filter((t) => t.length >= 8);
}

const MIN_NARRATIVE_CHARS = 40;
const MIN_NARRATIVE_WORDS = 6;
const MAX_NARRATIVE_ENTRIES = 10;
const NARRATIVE_SPARSE_GAP = 2;

const CHAT_MODE_SLUGS = new Set<string>(Object.keys(chatEntryTitleMap));
const CHAT_MODE_TITLES = new Set(
  Object.values(chatEntryTitleMap).map((title) => normalizeQuoteKey(title))
);
const ROUTING_HINTS = new Set(Object.values(chatEntryRoutingHintMap));

const CHIP_ACK_PATTERNS = [
  /^yes,?\s/i,
  /^no,?\s/i,
  /^tell me more/i,
  /^i['']?m ready/i,
  /^okay[.!]?$/i,
  /^ok[.!]?$/i,
  /^sure[.!]?$/i,
  /^thanks?[.!]?$/i,
  /^thank you[.!]?$/i,
  /^continue[.!]?$/i,
  /^go ahead[.!]?$/i,
  /^start[.!]?$/i,
  /^let['']?s (go|start|begin)/i,
];

const REFLECTIVE_SIGNAL_PATTERNS = [
  /\bi (realized|noticed|think|felt|learned|decided|remember|understand|kept|stopped|started)\b/i,
  /\bwhat made (the|a) difference\b/i,
  /\bi used to\b/i,
  /\bthe hardest part\b/i,
  /\bfor me\b/i,
  /\b(exhausted|overwhelmed|grateful|proud|anxious|frustrated|hopeful|drained|steady)\b/i,
  /\b(student|students|parent|parents|class|meeting|colleague|period|lesson|team)\b/i,
];

function normalizeQuoteKey(text: string): string {
  return text.toLowerCase().replace(/[_\s-]+/g, " ").trim();
}

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function isChipAcknowledgment(text: string): boolean {
  const trimmed = text.trim();
  return CHIP_ACK_PATTERNS.some((pattern) => pattern.test(trimmed));
}

function isModeArtifact(text: string): boolean {
  const trimmed = text.trim();
  const normalized = normalizeQuoteKey(trimmed);

  if (CHAT_MODE_SLUGS.has(trimmed)) return true;
  if (ROUTING_HINTS.has(trimmed)) return true;
  if (CHAT_MODE_TITLES.has(normalized)) return true;

  for (const slug of CHAT_MODE_SLUGS) {
    if (normalizeQuoteKey(slug) === normalized) return true;
  }
  return false;
}

/** Whether a user turn has enough substance to appear in the growth narrative. */
export function isSubstantiveUserQuote(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < MIN_NARRATIVE_CHARS) return false;
  if (wordCount(trimmed) < MIN_NARRATIVE_WORDS) return false;
  if (isModeArtifact(trimmed)) return false;
  if (isChipAcknowledgment(trimmed)) return false;
  return true;
}

/** Score how reflective a quote is — higher wins when picking the best turn per session. */
export function scoreReflectiveQuote(text: string): number {
  let score = 0;
  for (const pattern of REFLECTIVE_SIGNAL_PATTERNS) {
    if (pattern.test(text)) score += 2;
  }
  if (text.length >= 80) score += 1;
  if (text.length >= 120) score += 1;
  return score;
}

/** Pick the most reflective qualifying user message from a session transcript. */
export function pickBestUserQuote(messages: string[]): string | null {
  let best: { text: string; score: number } | null = null;

  for (const text of messages) {
    if (!isSubstantiveUserQuote(text)) continue;
    const score = scoreReflectiveQuote(text);
    if (
      !best ||
      score > best.score ||
      (score === best.score && text.length > best.text.length)
    ) {
      best = { text, score };
    }
  }

  return best?.text ?? null;
}

function pillarTagForMode(mode: ChatEntry): string {
  const pillarId = pillarForMode(mode);
  return PILLAR_META[pillarId]?.categoryLabel ?? "Reflection";
}

function narrativeToneForMode(mode: ChatEntry): NarrativeEntry["tone"] {
  if (mode.includes("students") || mode.includes("voice")) return "relational";
  return "purposive";
}

function clipQuote(text: string, max = 160): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
}

function segmentsForCount(sessionCount: number, resonateCount: number): number {
  const fromSessions = sessionCount === 0 ? 0 : Math.min(4, Math.ceil(sessionCount / 2));
  const fromFeedback = Math.min(4, resonateCount);
  return Math.min(4, Math.max(fromSessions, fromFeedback));
}

function pillarForMode(mode: ChatEntry): string {
  for (const [pillarId, modes] of Object.entries(PILLAR_MODES)) {
    if (modes.includes(mode)) return pillarId;
  }
  return "agency-authorship";
}

function deriveGrowthPatterns(
  sessions: UserSession[],
  userId: string
): GrowthPattern[] {
  const resonatesByPillar = listMirrorInsightResponses(userId).filter(
    (r) => r.feedback === "resonates"
  );

  return Object.entries(PILLAR_META).map(([id, meta]) => {
    const modes = PILLAR_MODES[id] ?? [];
    const pillarSessions = sessions.filter((s) => modes.includes(s.chat_mode));
    const resonateCount = resonatesByPillar.filter((r) => r.insightId.startsWith(`mirror-${id}-`)).length;
    const filled = segmentsForCount(pillarSessions.length, resonateCount);
    const active = pillarSessions.length > 0;

    return {
      id,
      categoryLabel: meta.categoryLabel,
      insightTitle: active
        ? meta.defaultTitle
        : `Quiet for now — ${meta.categoryLabel.toLowerCase()} grows when you use those tools`,
      segmentsFilled: filled,
      footerNote: active
        ? `${pillarSessions.length} session${pillarSessions.length === 1 ? "" : "s"} in this space${
            filled > 0 ? `. ${filled} of 4 stages reflected.` : "."
          }`
        : meta.defaultFooter,
      accent: meta.accent,
    };
  });
}

function deriveMirrorQueue(
  sessions: Array<{ session: UserSession; userMessages: string[] }>,
  userId: string
): SessionMirrorPrompt[] {
  const answered = answeredInsightIds(userId);
  const mirrors: SessionMirrorPrompt[] = [];

  for (const { session, userMessages } of sessions) {
    const quote = pickBestUserQuote(userMessages);
    if (!quote) continue;

    const pillarId = pillarForMode(session.chat_mode);
    const id = `mirror-${pillarId}-${session.id}`;
    if (answered.has(id)) continue;

    mirrors.push({
      id,
      eyebrow: "From your own words — does this still feel true?",
      quote: `You wrote: "${clipQuote(quote)}"`,
    });
  }

  return mirrors.slice(0, 5);
}

function deriveLanguageShifts(allUserTexts: string[]): LanguageShift[] {
  const substantive = allUserTexts.filter(isSubstantiveUserQuote);
  if (substantive.length < 2) return [];

  const first = substantive[0]!;
  const last = substantive[substantive.length - 1]!;
  if (first === last) return [];

  return [
    {
      id: "derived-first-vs-latest",
      before: clipQuote(first, 100),
      after: clipQuote(last, 100),
    },
  ];
}

function deriveNarrativeEntries(
  sessions: Array<{ session: UserSession; userMessages: string[]; lastActivity: Date }>,
  userId: string
): NarrativeEntry[] {
  const entries: NarrativeEntry[] = [];
  const resonateCount = listMirrorInsightResponses(userId).filter(
    (r) => r.feedback === "resonates"
  ).length;

  const chronological = [...sessions].sort(
    (a, b) =>
      new Date(a.session.created_at).getTime() - new Date(b.session.created_at).getTime()
  );

  const earliestWithQuote = chronological.find(({ userMessages }) => pickBestUserQuote(userMessages));
  if (earliestWithQuote) {
    const startQuote = pickBestUserQuote(earliestWithQuote.userMessages)!;
    entries.push({
      id: `narrative-start-${earliestWithQuote.session.id}`,
      kind: "reflection",
      quote: clipQuote(startQuote),
      when: formatDistanceToNowStrict(new Date(earliestWithQuote.session.created_at), {
        addSuffix: true,
      }),
      isStart: true,
    });
  }

  type Candidate = {
    session: UserSession;
    quote: string;
    score: number;
    lastActivity: Date;
  };

  const candidates: Candidate[] = [];
  for (const { session, userMessages, lastActivity } of sessions) {
    const quote = pickBestUserQuote(userMessages);
    if (!quote) continue;
    candidates.push({
      session,
      quote,
      score: scoreReflectiveQuote(quote),
      lastActivity,
    });
  }

  candidates.sort(
    (a, b) => b.lastActivity.getTime() - a.lastActivity.getTime()
  );

  const selected: Candidate[] = [];
  let skippedSinceLast = 0;

  for (const candidate of candidates) {
    if (candidate.session.id === earliestWithQuote?.session.id) continue;

    const include = candidate.score >= 2 || skippedSinceLast >= NARRATIVE_SPARSE_GAP;
    if (!include) {
      skippedSinceLast += 1;
      continue;
    }

    selected.push(candidate);
    skippedSinceLast = 0;

    if (selected.length >= MAX_NARRATIVE_ENTRIES) break;
  }

  for (const { session, quote, score, lastActivity } of selected) {
    entries.push({
      id: `narrative-${session.id}`,
      kind: score >= 3 ? "reflection" : "practice",
      quote: clipQuote(quote),
      caption: session.session_name?.trim() || undefined,
      when: formatDistanceToNowStrict(lastActivity, { addSuffix: true }),
      tag: pillarTagForMode(session.chat_mode),
      tone: narrativeToneForMode(session.chat_mode),
    });
  }

  const qualifyingCount = candidates.length;
  if (resonateCount >= 2 && qualifyingCount >= 3) {
    const strongest = [...candidates].sort((a, b) => b.score - a.score)[0];
    if (strongest && strongest.score >= 3) {
      entries.unshift({
        id: "narrative-milestone-affirmed",
        kind: "milestone",
        quote: clipQuote(strongest.quote),
        caption: "You confirmed reflections that resonate — and kept writing from that place.",
        when: formatDistanceToNowStrict(strongest.lastActivity, { addSuffix: true }),
        tag: "+ milestone",
      });
    }
  } else if (qualifyingCount >= 8) {
    const strongest = [...candidates].sort((a, b) => b.score - a.score)[0];
    if (strongest) {
      entries.unshift({
        id: "narrative-milestone-depth",
        kind: "milestone",
        quote: clipQuote(strongest.quote),
        caption: "A thread worth naming — drawn from your own reflective writing across sessions.",
        when: formatDistanceToNowStrict(strongest.lastActivity, { addSuffix: true }),
        tag: "+ milestone",
      });
    }
  }

  return entries;
}

/** Build Insights dashboard content from persisted session + chat history rows. */
export async function buildDerivedInsightsDashboard(
  historySessions: UserSession[],
  userId: string
): Promise<DerivedInsightsDashboard> {
  if (!userId || historySessions.length === 0) {
    return {
      mirrorQueue: [],
      patterns: deriveGrowthPatterns([], userId),
      languageShifts: [],
      narrativeEntries: [],
      sessionsWithMessages: 0,
      milestonesCount: 0,
    };
  }

  const sorted = [...historySessions].sort(
    (a, b) =>
      new Date(b.closed_at ?? b.created_at).getTime() -
      new Date(a.closed_at ?? a.created_at).getTime()
  );

  const enriched = await Promise.all(
    sorted.map(async (session) => {
      try {
        const records = await chatHistoryService.getChatHistoryBySessionId(session.id);
        const userMessages = extractUserMessages(records);
        const lastRow = records[records.length - 1];
        const lastActivity = lastRow
          ? new Date(lastRow.message.timestamp)
          : new Date(session.closed_at ?? session.created_at);
        return { session, userMessages, lastActivity, records };
      } catch {
        return {
          session,
          userMessages: [] as string[],
          lastActivity: new Date(session.closed_at ?? session.created_at),
          records: [] as ChatHistoryRecord[],
        };
      }
    })
  );

  const withMessages = enriched.filter((e) => e.userMessages.length > 0);
  const allUserTexts = withMessages.flatMap((e) => e.userMessages);

  const narrativeEntries = deriveNarrativeEntries(withMessages, userId);
  const milestonesCount = narrativeEntries.filter((e) => e.kind === "milestone").length;

  return {
    mirrorQueue: deriveMirrorQueue(withMessages, userId),
    patterns: deriveGrowthPatterns(
      withMessages.map((e) => e.session),
      userId
    ),
    languageShifts: deriveLanguageShifts(allUserTexts),
    narrativeEntries,
    sessionsWithMessages: withMessages.length,
    milestonesCount,
  };
}
